import { FeatureFlag } from '@/types/featureFlag';
import { atomWithLocalStorage } from '@/utils/browserStorage';
import { appStore } from '@/utils/jotaiStore';
import { atom, useAtomValue } from 'jotai';
import { useEffect } from 'react';
import { z } from 'zod';

import { isFeatureFlag } from './flagCatalog';
import { areFlagOverridesAvailable } from './flagOverrideGate';
import { FlagOverrides, sanitizeFlagOverrides, withFlagOverride } from './flagOverrides';

/* The state behind the flag override tools: what is forced, whether the tools are unlocked,
 * what the server last said, which flags the app has asked for, and whether the panel is
 * open.
 *
 * Written through `appStore` as well as through hooks, because the console API drives the same
 * state from outside React.
 */

const OVERRIDES_STORAGE_KEY = 'featureFlagOverrides';

// One shared empty record, so a locked session hands every reader the same identity instead of
// a fresh object per render.
const NO_OVERRIDES: FlagOverrides = {};

const storedOverridesAtom = atomWithLocalStorage(
    OVERRIDES_STORAGE_KEY,
    {} as Record<string, boolean>,
    z.record(z.string(), z.boolean()),
);

export const flagOverridesAtom = atom(
    (get) => sanitizeFlagOverrides(get(storedOverridesAtom)),
    (_get, set, overrides: FlagOverrides) => {
        set(storedOverridesAtom, { ...overrides });
    },
);

export const isUnlockedAtom = atom(false);

export const isPanelOpenAtom = atom(false);

// What the server resolved for every flag, or undefined until an answer arrives.
export const serverFlagValuesAtom = atom<Partial<Record<FeatureFlag, boolean>> | undefined>(
    undefined,
);

/* The flags something in the app has actually asked the server for so far this session.
 *
 * Most of the catalog is irrelevant to any given page, so knowing which flags the page in front
 * of you reads is the difference between forcing the flag that matters and hunting through 49
 * of them. It grows as more of the app renders and never shrinks, since a flag a page read is
 * still a flag that page reads.
 */
export const queriedFlagsAtom = atom<FeatureFlag[]>([]);

export const useQueriedFlags = (): FeatureFlag[] => useAtomValue(queriedFlagsAtom);

const recordQueriedFlags = (features: string[]) => {
    const known = appStore.get(queriedFlagsAtom);
    const added = features.filter(
        (feature) => isFeatureFlag(feature) && !known.includes(feature),
    ) as FeatureFlag[];
    if (added.length > 0) {
        appStore.set(queriedFlagsAtom, [...known, ...added]);
    }
};

/* Records what a flag hook asked for, from an effect rather than during the render that asked,
 * and only where the tools can use it.
 */
export const useRecordQueriedFlags = (features: string[], isQuerying: boolean) => {
    const signature = features.join(',');

    useEffect(() => {
        if (!areFlagOverridesAvailable || !isQuerying) {
            return;
        }
        recordQueriedFlags(signature.split(','));
    }, [signature, isQuerying]);
};

/* What the flag hooks layer over the server's answer. Nothing is forced unless the environment
 * allows the tools and the passphrase has unlocked them, so a session that never opens them
 * reads its flags exactly as it did before.
 */
export const effectiveFlagOverridesAtom = atom((get) =>
    areFlagOverridesAvailable && get(isUnlockedAtom) ? get(flagOverridesAtom) : NO_OVERRIDES,
);

export const useEffectiveFlagOverrides = (): FlagOverrides =>
    useAtomValue(effectiveFlagOverridesAtom);

export const getFlagOverrides = (): FlagOverrides => appStore.get(flagOverridesAtom);

export const setFlagOverride = (flag: FeatureFlag, value: boolean | null) => {
    appStore.set(flagOverridesAtom, withFlagOverride(getFlagOverrides(), flag, value));
};

export const clearFlagOverrides = () => {
    appStore.set(flagOverridesAtom, {});
};

export const getServerFlagValues = () => appStore.get(serverFlagValuesAtom);

export const getQueriedFlags = (): FeatureFlag[] => appStore.get(queriedFlagsAtom);

export const isOverridesUnlocked = (): boolean => appStore.get(isUnlockedAtom);

export const setOverridesUnlocked = (unlocked: boolean) => {
    appStore.set(isUnlockedAtom, unlocked);
};

export const setPanelOpen = (open: boolean) => {
    appStore.set(isPanelOpenAtom, open);
};
